import { client as clientHTTP } from "@/generated/services.gen";
import { getApiGoogleMapsConfig, getApiInitialConfig } from "@/generated";
import { handleApiResponse } from "./routers/handleApiResponse";
import { handleApiResponseAssertData } from "./routers/handleApiResponseAssertData";

import { apiUser } from "./routers/apiUser";
import { createApiDeals } from "./routers/apiDeals";
import { createApiExtractions } from "./routers/apiExtractions";
import { createApiAccounts } from "./routers/apiAccounts";
import { apiAnalytics } from "./routers/apiAnalytics";
import { apiDatabases } from "./routers/apiDatabases";
import { createApiScreenSessions } from "./routers/apiScreenSessions";

export const apiConfig = {
    getGoogleMapsConfig: async () => handleApiResponse(getApiGoogleMapsConfig),
    getInitialConfig: () =>
        handleApiResponseAssertData(
            getApiInitialConfig,
            undefined,
            "No auth config received",
        ),
};

export const createApiODIN = ({
    baseHttpUrl,
    baseWsUrl,
    requestInterceptors,
    getAccessToken,
}: {
    baseHttpUrl?: string;
    baseWsUrl: string;
    requestInterceptors?: Parameters<
        typeof clientHTTP.interceptors.request.use
    >[0][];
    getAccessToken: () => Promise<string>;
}) => {
    if (baseHttpUrl !== undefined) {
        clientHTTP.setConfig({
            baseUrl: baseHttpUrl,
        });
    }

    if (requestInterceptors) {
        requestInterceptors.forEach(interceptor => {
            clientHTTP.interceptors.request.use(interceptor);
        });
    }

    return {
        ...apiUser,
        ...createApiDeals({ getAccessToken, baseWsUrl }),
        ...createApiExtractions({ getAccessToken, baseWsUrl }),
        ...createApiScreenSessions({ getAccessToken, baseWsUrl }),
        ...createApiAccounts({ getAccessToken, baseWsUrl }),
        ...apiAnalytics,
        ...apiConfig,
        ...apiDatabases,
    };
};

export type TApiODIN = ReturnType<typeof createApiODIN>;

export type ApiError =
    | { message: string; code?: number }
    | { details: string; status: string };

export type {
    TRequestParamsSaveMappings,
    TRequestParamsUpdateExtraction,
    TRequestParamsSaveLabelset,
    TRequestParamsSetDocumentTargetRow,
} from "./routers/apiExtractions";

export type { TExportConfig } from "./routers/types";

export type {
    PostApiDatabasesManualDatabaseSyncResponse as TResponseSyncDatabases,
    PostApiDatabasesManualDatabaseSyncError as TErrorSyncDatabases,
    ExtractionTableContents as TResponseGetExtraction,
    StandardizedTableContents as TResponseGetStandardisedExtraction,
    PutApiUserPreferencesUserSelectedDatabasesResponse as TResponseUserSelectedDatabases,
    PutApiUserPreferencesUserSelectedDatabasesError as TErrorUserSelectedDatabases,
    PutApiUserPreferencesModelResponse as TResponseSetUserModel,
    PutApiUserPreferencesModelError as TErrorSetUserModel,
    NewProject as TNewProject,
    FileUploadResponse as TFileUploadResponse,
    ProjectInfo as TProjectInfo,
    BasePropertyInfoWithId as TMultipropPropertyInfo,
    MultipropDeal as TMultipropDeal,
    GetApiProjectsResponse as TResponseGetDeals,
    GetApiProjectsError as TErrorGetDeals,
    GetApiProjectsByProjectIdPropertyResponse as TResponseGetDeal,
    GetApiProjectsByProjectIdPropertyError as TErrorGetDeal,
    DeleteApiProjectsByProjectIdResponse as TResponseDeleteDeal,
    DeleteApiProjectsByProjectIdError as TErrorDeleteDeal,
    DeleteApiExtractionsDiscardResponse as TResponseDeleteExtractions,
    DeleteApiExtractionsDiscardError as TErrorDeleteExtractions,
    GetApiProjectsByProjectIdFilesResponse as TResponseGetDealFiles,
    GetApiProjectsByProjectIdFilesError as TErrorGetDealFiles,
    ProjectNameCheckResponse as TResponseProjectNameCheck,
    PropertyAddressCheckResponse as TResponsePropertyAddressCheck,
    PropertyNameCheckResponse as TResponsePropertyNameCheck,
    PostApiProjectsProjectNameExistsError as TErrorProjectNameCheck,
    PostApiProjectsPropertyNameExistsError as TErrorPropertyNameCheck,
    UpdateExtractionResponse as TResponseUpdateExtraction,
    PostApiExtractionsByExtractionIdError as TErrorUpdateExtraction,
    ExtractionStatus as TExtractionStatus,
    GetApiExtractionsByExtractionIdError as TErrorViewExtraction,
    GetApiExtractionsByExtractionIdResponse as TResponseViewExtraction,
    GetApiExtractionsByExtractionIdStandardizedError as TErrorViewStandardizedExtraction,
    GetApiExtractionsByExtractionIdStandardizedResponse as TResponseViewStandardizedExtraction,
    AccountMapping as TResponseGetExtractionAccountMapping,
    PostApiExtractionsByExtractionIdOutliersError as TErrorDismissOutliers,
    PostApiExtractionsByExtractionIdOutliersResponse as TResponseDismissOutliers,
    GetApiProjectsByProjectIdOperatingStatementResponse as TResponseGetOperatingStatement,
    GetApiProjectsByProjectIdOperatingStatementError as TErrorGetOperatingStatement,
    GetApiProjectsByProjectIdExtractionByExtractionIdOverlappingPeriodsResponse as TResponseGetOverlappingPeriods,
    PutApiUserUserConsentResponse as TResponsePutUserConsent,
    PutApiUserUserConsentError as TErrorPutUserConsent,
    GetApiUserUserDataResponse as TResponseGetUserData,
    GetApiUserUserDataError as TErrorGetUserData,
    GetApiGoogleMapsConfigError as TErrorGetGoogleMapsConfig,
    GetApiGoogleMapsConfigResponse as TResponseGetGoogleMapsConfig,
    GetApiInitialConfigResponse as TResponseGetInitialConfig,
    GetApiInitialConfigError as TErrorGetInitialConfig,
    PostApiUserLoginCallbackResponse as TResponseLoginCallback,
    PostApiUserLoginCallbackError as TErrorLoginCallback,
    HTTPValidationError as TErrorValidation,
    DealPropertyInfo as TDealPropertyInfo,
    ProjectNameCheckInfo as TProjectNameCheckInfo,
    PropertyAddressCheckInfo as TPropertyAddressCheckInfo,
    PropertyNameCheckInfo as TPropertyNameCheckInfo,
    ExtractionInfo as TExtractionInfo,
    UserDatabase as TUserDatabase,
    StandardizedRow as TStandardizedRow,
    StandardizedTotalColumn as TStandardizedTotalColumn,
    AnnualizationTotalColumn as TAnnualizationTotalColumn,
    AnnualizationPeriodColumn as TAnnualizationPeriodColumn,
    NewProperty as TNewProperty,
    PropertyInfo as TPropertyInfo,
    PropertySummary as TPropertySummary,
    DealView as TDealView,
    FileInfo as TFileInfo,
    DealPropertyInfoWithId as TDealPropertyInfoWithId,
    FileList as TFileList,
    GetApiAnalyticsByFilterTypeError as TErrorGetDashboardAnalytics,
    GetApiAnalyticsByFilterTypeResponse as TResponseGetDashboardAnalytics,
    GetApiProjectsByProjectIdExtractionByExtractionIdConflictedExtractionsResponse as TResponseGetConflictExtractions,
    GetApiProjectsByProjectIdExtractionByExtractionIdConflictedExtractionsError as TErrorGetConflictExtractions,
    PostApiProjectsByProjectIdExtractionByExtractionIdConvertToDraftResponse as TResponseConvertToDraft,
    PostApiProjectsByProjectIdExtractionByExtractionIdConvertToDraftError as TErrorConvertToDraft,
    PutApiUserPreferencesUserSelectedDatabasesResponse as TResponseSetUserSelectedDatabases,
    PutApiUserPreferencesUserSelectedDatabasesError as TErrorSetUserSelectedDatabases,
    UserInfo as TUserInfo,
    PublicAuth0Config as TPublicAuth0Config,
    PostApiExtractionsByExtractionIdLabelsetError as TErrorSaveLabelset,
    PostApiExtractionsByExtractionIdLabelsetResponse as TResponseSaveLabelset,
    GetApiExtractionsByExtractionIdLabelsetError as TErrorGetLabelset,
    GetApiExtractionsByExtractionIdLabelsetResponse as TResponseGetLabelset,
    LabelsetInfo as TLabelsetInfo,
    AccountMapping as TAccountMapping,
    AccountMappingBase as TAccountMappingBase,
    GetApiDatabasesByDatabaseIdCoaDefinitionsResponse as TResponseGetAvailableCoAsByDatabaseId,
    GetApiDatabasesByDatabaseIdCoaDefinitionsError as TErrorGetAvailableCoAsByDatabaseId,
    GetApiCoaAccountsByCoaIdResponse as TResponseGetAccountsByCoaId,
    GetApiCoaAccountsByCoaIdError as TErrorGetAccountsByCoaId,
    GetApiCoaResponse as TResponseGetAvailableCoAs,
    GetApiCoaError as TErrorGetAvailableCoAs,
    GetApiCoaAuditLogsByCoaIdResponse as TResponseGetAuditLogsByCoaId,
    GetApiCoaAuditLogsByCoaIdError as TErrorGetAuditLogsByCoaId,
    TableLocation as TTableLocation,
    PostApiExtractionsByExtractionIdTableResponse as TResponseChangeTable,
    PostApiExtractionsByExtractionIdTableError as TErrorChangeTable,
    UserConsent as TUserConsent,
    GetApiDatabasesAdminAllResponse as TResponseGetAllDatabases,
    GetApiDatabasesAdminAllError as TErrorGetAllDatabases,
    RevokeAccountPermissionsRequest as TRevokeAccountPermissionsRequest,
    GrantAccountPermissionsRequest as TGrantAccountPermissionsRequest,
    PostApiCoaAccountsPermissionsResponse as TResponseAddAccountPermissions,
    PostApiCoaAccountsPermissionsError as TErrorAddAccountPermissions,
    DeleteApiCoaAccountsPermissionsResponse as TResponseDeleteAccountPermissions,
    DeleteApiCoaAccountsPermissionsError as TErrorDeleteAccountPermissions,
    OperatingStatementPeriod as TOperatingStatementPeriod,
    GetApiProjectsByProjectIdByAnnualizationTypeOperatingStatementPeriodsResponse as TResponseGetOperatingStatementPeriods,
    GetApiProjectsByProjectIdByAnnualizationTypeOperatingStatementPeriodsError as TErrorGetOperatingStatementPeriods,
    SaveAlteredCustomAccountsRequest as TSaveAlteredCustomAccountsRequestBody,
    PostApiCoaAccountsMappingsByCoaIdError as TErrorSaveAlteredCustomAccounts,
    PostApiCoaAccountsMappingsByCoaIdResponse as TResponseSaveAlteredCustomAccounts,
    ChartOfAccountsDefinition as TChartOfAccountsDefinition,
    PostApiDatabasesAdminCoaDefinitionsResponse as TResponsePostAdminCoaDefinitions,
    PostApiDatabasesAdminCoaDefinitionsError as TErrorPostAdminCoaDefinitions,
    FileType as TFileType,
    AnnualizationType as TAnnualizationType,
    ExtractionType as TExtractionType,
    CountryCode as TCountryCode,
    AddressComponent as TAddressComponent,
    MappedRowWithInference as TMappedRowWithInference,
    NewCoADefinition as TNewCoADefinition,
    PostApiCoaDraftCoaResponse as TResponseCreateDraftCoa,
    PostApiCoaDraftCoaError as TErrorCreateDraftCoa,
    GetApiCoaCoaDefinitionByCoaIdResponse as TResponseGetCoaDefinitionByCoaId,
    GetApiCoaCoaDefinitionByCoaIdError as TErrorGetCoaDefinitionByCoaId,
    PostApiCoaDraftCoaAccountsByCoaIdResponse as TResponseInsertCoaAccounts,
    PostApiCoaDraftCoaAccountsByCoaIdError as TErrorInsertCoaAccounts,
    PostApiCoaCompleteCoaByCoaIdResponse as TResponseCompleteCoA,
    PostApiCoaCompleteCoaByCoaIdError as TErrorCompleteCoA,
    DraftChartOfAccountsDetail as TDraftChartOfAccountsDetail,
    GetApiDatabasesAdminCompaniesResponse as TResponseGetCompanies,
    GetApiDatabasesAdminCompaniesError as TErrorGetCompanies,
    Company as TCompany,
    DeleteApiCoaDraftCoaByCoaIdResponse as TResponseDeleteDraftCoA,
    DeleteApiCoaDraftCoaByCoaIdError as TErrorDeleteDraftCoA,
    PutApiCoaDraftCoaByCoaIdResponse as TResponseUpdateDraftCoA,
    PutApiCoaDraftCoaByCoaIdError as TErrorUpdateDraftCoA,
    AccountMappingTableData as TAccountMappingTableData,
    PutApiExtractionsByExtractionIdRowTypeResponse as TResponseUpdateRowType,
    PutApiExtractionsByExtractionIdRowTypeError as TErrorUpdateRowType,
    RowType as TRowType,
    PutApiExtractionsByExtractionIdResetRowChangesResponse as TResponseResetRowChanges,
    PutApiExtractionsByExtractionIdResetRowChangesError as TErrorResetRowChanges,
    BetaTesterFeature as TBetaTesterFeature,
    StandardizedMonthColumn as TStandardizedMonthColumn,
    StandardizedYearColumn as TStandardizedYearColumn,
    StandardizedCustomPeriodTrailingColumn as TStandardizedCustomPeriodTrailingColumn,
    StandardizedCustomPeriodBudgetColumn as TStandardizedCustomPeriodBudgetColumn,
    StandardizedTrailingYearToDateColumn as TStandardizedTrailingYearToDateColumn,
    StandardizedBudgetYearToDateColumn as TStandardizedBudgetYearToDateColumn,
    MonthYear as TMonthYear,
    PutApiExtractionsByExtractionIdColumnStateData as TRequestParamsSetColumnState,
    PostApiProjectsByProjectIdFilesUploadResponse as TResponseAddProjectFile,
    PostApiProjectsByProjectIdFilesUploadError as TErrorAddProjectFile,
    PostApiProjectsByProjectIdFilesByFileIdExtractRentRollResponse as TResponseCreateRentRollExtractionByProjectId,
    PostApiProjectsByProjectIdFilesByFileIdExtractRentRollError as TErrorCreateRentRollExtractionByProjectId,
    GetApiProjectsPropertyInfoForDeleteByPropertyIdResponse as TResponseGetPropertyInfoForDelete,
    GetApiProjectsPropertyInfoForDeleteByPropertyIdError as TErrorGetPropertyInfoForDelete,
    CreateProjectResponse as TCreateProjectResponse,
    LLMValidationSummary as TLLMValidationSummary,
    LLMDiagnosisSummary as TLLMDiagnosisSummary,
} from "@/generated";
